import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import useAxios from "../../../utility/axios-token-manager/init";

const PRIMARY = "#a7cc48";

const STATUS_COLOR = {
  open:   { bg: "#d1fae5", text: "#065f46", border: "#6ee7b7" },
  closed: { bg: "#f3f4f6", text: "#6b7280", border: "#e5e7eb" },
};

export default function SupportTickets() {
  const history = useHistory();
  const [tickets, setTickets] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (statusFilter) params.append("status", statusFilter);
      const res = await useAxios.get(`/admin/support-tickets?${params}`);
      if (res.data.success) {
        setTickets(res.data.tickets);
        setTotal(res.data.total);
      }
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div style={S.wrap}>
      {/* Header */}
      <div style={S.header}>
        <div>
          <h2 style={S.title}>Support Tickets</h2>
          <p style={S.sub}>{total} total ticket{total !== 1 ? "s" : ""}</p>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {["", "open", "closed"].map((s) => (
            <button
              key={s}
              onClick={() => { setStatusFilter(s); setPage(1); }}
              style={{
                ...S.filterBtn,
                background: statusFilter === s ? PRIMARY : "#fff",
                color:      statusFilter === s ? "#fff"  : "#374151",
                borderColor: statusFilter === s ? PRIMARY : "#d1d5db",
              }}
            >
              {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button onClick={fetchTickets} style={{ ...S.filterBtn, color: "#374151", borderColor: "#d1d5db" }}>
            ↻ Refresh
          </button>
        </div>
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {loading ? (
        <div style={S.center}><div style={S.spinner} /></div>
      ) : tickets.length === 0 ? (
        <div style={S.center}><p style={{ color: "#9ca3af" }}>No tickets found.</p></div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={S.table}>
            <thead>
              <tr style={S.thead}>
                {["Title", "Merchant", "Status", "Messages", "Last Updated", ""].map((h) => (
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tickets.map((t) => {
                const col = STATUS_COLOR[t.status] || STATUS_COLOR.open;
                return (
                  <tr key={t._id} style={S.tr}>
                    <td style={{ ...S.td, maxWidth: 280 }}>
                      <div style={S.ticketTitle}>{t.title}</div>
                      {t.lastMessage && (
                        <div style={S.preview}>
                          {t.lastMessage.sender === "admin" ? "You: " : "Merchant: "}
                          {t.lastMessage.message}
                        </div>
                      )}
                    </td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 500, color: "#111827" }}>{t.merchantName || "—"}</div>
                      <div style={{ color: "#6b7280", fontSize: 12 }}>{t.merchantEmail}</div>
                    </td>
                    <td style={S.td}>
                      <span style={{ ...S.badge, background: col.bg, color: col.text, border: `1px solid ${col.border}` }}>
                        {t.status.charAt(0).toUpperCase() + t.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ ...S.td, color: "#374151" }}>{t.messageCount}</td>
                    <td style={{ ...S.td, color: "#6b7280", fontSize: 13, whiteSpace: "nowrap" }}>
                      {new Date(t.updatedAt).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" })}
                    </td>
                    <td style={S.td}>
                      <button
                        onClick={() => history.push(`/admin/support-tickets/${t._id}`)}
                        style={S.viewBtn}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {totalPages > 1 && (
        <div style={S.pagination}>
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} style={S.pageBtn}>← Prev</button>
          <span style={{ color: "#374151", fontSize: 14 }}>Page {page} of {totalPages}</span>
          <button onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={S.pageBtn}>Next →</button>
        </div>
      )}
    </div>
  );
}

const S = {
  wrap:    { fontFamily: "sans-serif", padding: "0 4px" },
  header:  { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24, flexWrap: "wrap", gap: 12 },
  title:   { margin: 0, fontSize: 22, fontWeight: 700, color: "#111827" },
  sub:     { margin: "4px 0 0", color: "#6b7280", fontSize: 14 },
  filterBtn: { padding: "6px 14px", borderRadius: 20, border: "1px solid", background: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" },
  errorBox:  { background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, padding: "12px 16px", color: "#dc2626", marginBottom: 16 },
  center:    { textAlign: "center", padding: "60px 0" },
  spinner:   { width: 36, height: 36, border: "3px solid #e5e7eb", borderTop: `3px solid ${PRIMARY}`, borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" },
  table:     { width: "100%", borderCollapse: "collapse", fontSize: 14 },
  thead:     { background: "#f9fafb", borderBottom: "1px solid #e5e7eb" },
  th:        { padding: "10px 14px", textAlign: "left", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" },
  tr:        { borderBottom: "1px solid #f3f4f6" },
  td:        { padding: "12px 14px", verticalAlign: "top" },
  ticketTitle: { fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  preview:   { color: "#6b7280", fontSize: 12, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  badge:     { padding: "3px 10px", borderRadius: 20, fontWeight: 600, fontSize: 12 },
  viewBtn:   { padding: "6px 14px", background: PRIMARY, color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer" },
  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: 10, marginTop: 20 },
  pageBtn:   { padding: "6px 14px", border: "1px solid #e5e7eb", borderRadius: 8, cursor: "pointer", background: "#fff", fontSize: 14 },
};
